import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8 md:p-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: November 21, 2025</p>

        <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing and using BookingFlows, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
            <p className="leading-relaxed">
              BookingFlows is a booking and scheduling platform designed for tours, rentals, classes, venues, and service-based businesses. We provide:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Online booking widgets and scheduling tools</li>
              <li>Payment processing through Stripe</li>
              <li>Digital waiver collection with verification</li>
              <li>Staff scheduling and coordination</li>
              <li>Customer relationship management</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Account Registration</h2>
            <p className="leading-relaxed">
              To use BookingFlows, you must create an account with accurate information. You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Pricing and Payments</h2>
            <p className="leading-relaxed">
              BookingFlows operates on a transaction-based fee model:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>No monthly fees or setup costs</li>
              <li>3% platform fee on customer bookings (paid by customer)</li>
              <li>Payment processing handled securely through Stripe</li>
              <li>Funds are deposited directly to your Stripe account</li>
            </ul>
            <p className="leading-relaxed mt-4">
              We reserve the right to modify our pricing with 30 days notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Digital Waivers</h2>
            <p className="leading-relaxed">
              Our digital waiver system collects device fingerprints, IP addresses, and signature data for legal verification. By using this feature:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You agree to comply with all applicable laws regarding liability waivers</li>
              <li>You are responsible for the legal validity of your waiver text</li>
              <li>We provide the technology; you are responsible for legal compliance</li>
              <li>We recommend consulting with legal counsel for waiver content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Acceptable Use</h2>
            <p className="leading-relaxed">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the service for any illegal purpose</li>
              <li>Attempt to access unauthorized data or accounts</li>
              <li>Interfere with the proper functioning of the service</li>
              <li>Upload malicious code or viruses</li>
              <li>Scrape or harvest data without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Service Availability</h2>
            <p className="leading-relaxed">
              We strive for 99.9% uptime but do not guarantee uninterrupted service. We are not liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Service interruptions or downtime</li>
              <li>Data loss (though we maintain regular backups)</li>
              <li>Third-party service failures (Stripe, Twilio, etc.)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Intellectual Property</h2>
            <p className="leading-relaxed">
              BookingFlows and its original content, features, and functionality are owned by BookingFlows and are protected by copyright, trademark, and other laws. Your data and customer information remain your property.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="leading-relaxed">
              BookingFlows is provided "as is" without warranties of any kind. We are not liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Lost revenue or bookings</li>
              <li>Customer disputes or legal claims</li>
              <li>Waiver validity or legal enforceability</li>
              <li>Third-party service failures</li>
            </ul>
            <p className="leading-relaxed mt-4">
              Our total liability shall not exceed the fees you paid in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Termination</h2>
            <p className="leading-relaxed">
              You may cancel your account at any time. We may suspend or terminate your account if you violate these terms. Upon termination:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your access to the service will be disabled</li>
              <li>We will retain your data for 30 days before deletion</li>
              <li>You can export your data during this period</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Changes to Terms</h2>
            <p className="leading-relaxed">
              We may modify these terms at any time. We will notify you of material changes via email. Continued use of the service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Governing Law</h2>
            <p className="leading-relaxed">
              These terms are governed by the laws of the United States. Any disputes shall be resolved through binding arbitration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact</h2>
            <p className="leading-relaxed">
              For questions about these Terms of Service, contact us at:
            </p>
            <p className="font-semibold">hello@noreply.bookingflows.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
